import { BreakpointObserver } from "@angular/cdk/layout";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { ExternalAddressService } from "@onsip/common/services/api/resources/externalAddress/external-address.service";
import { UserAliasService } from "@onsip/common/services/api/resources/userAlias/user-alias.service";
import { E2eLocators } from "@onsip/e2e/configs/locators";
import { AdminTableAbstractComponent } from "@onsip/web/features/abstractClass/admin-table-abstract.component";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";
import { FormatExtensionsPipe } from "@onsip/web/features/shared/pipes/format-extensions.pipe";
import { BehaviorSubject, combineLatest, Subscription } from "rxjs";
import { ExternalAddressExtended } from "../interfaces/external-address-extended";

@Component({
  selector: "onsip-external-sip-address-table",
  templateUrl: "./external-sip-address-table.component.html",
  styleUrls: ["./external-sip-address-table.component.scss"],
  providers: [FormatExtensionsPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ExternalSipAddressTableComponent
  extends AdminTableAbstractComponent<ExternalAddressExtended>
  implements OnInit, OnDestroy
{
  hasExternalAddress$ = new BehaviorSubject(false);
  isEditMode$ = new BehaviorSubject<boolean>(false);

  E2eLocators = E2eLocators;

  protected displayedColumnsRecord = {
    XSmall: ["name", "actions"],
    Small: ["name", "address", "actions"],
    Medium: ["name", "extensions", "address", "actions"],
    Large: ["name", "extensions", "address", "actions"],
    XLarge: ["name", "extensions", "address", "actions"]
  };
  protected unsubscriber = new Subscription();

  constructor(
    private externalAddressService: ExternalAddressService,
    private snackbarService: SnackbarService,
    private userAliasService: UserAliasService,
    private formatExtensions: FormatExtensionsPipe,
    protected translate: TranslateService,
    protected breakpointObserver: BreakpointObserver,
    protected dialog: MatDialog,
    protected cdr: ChangeDetectorRef
  ) {
    super(breakpointObserver, cdr, dialog, translate);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.setDataFilter();
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  trackByAddressId(index: number, address: ExternalAddressExtended): string {
    return address.addressId;
  }

  protected performDeleteRequest(address: ExternalAddressExtended): void {
    this.externalAddressService.externalAddressDelete(address.aor).then(response => {
      if (response.status === "success") {
        this.snackbarService.openSnackBar(
          `External Sip Address ${address.name} was deleted`,
          "success"
        );
      } else {
        this.snackbarService.openSnackBar(
          `External Sip Address ${address.name} wasn't deleted`,
          "error"
        );
      }
    });
  }

  protected getItemName({ name }: ExternalAddressExtended): string {
    return name;
  }

  protected getItemValueByColumn(item: ExternalAddressExtended, property: string): string | number {
    switch (property) {
      case "name":
        return item.name.toLowerCase();
      case "extensions":
        return item?.extensions.join(", ").toLowerCase();
      case "sipAddress":
        return item.aor.toLowerCase();
      default:
        return item.name.toLowerCase();
    }
  }

  protected initDataSource(): void {
    this.externalAddressService.externalAddressBrowse(10000);
    this.userAliasService.userAliasBrowse();

    this.unsubscriber.add(
      combineLatest({
        addresses: this.externalAddressService.orgState,
        userAliases: this.userAliasService.orgState
      }).subscribe(({ addresses, userAliases }) => {
        this.dataSource.data = addresses.map(address => ({
          ...address,
          extensions: userAliases
            .filter(extension => extension.addressId === address.addressId)
            .map(filteredExtension => filteredExtension.aliasUsername)
        }));
        this.isDataObtained$.next(true);
        this.hasExternalAddress$.next(!!addresses.length);
      })
    );
  }

  protected setDataFilter(): void {
    this.dataSource.filterPredicate = (data, filter) => {
      const filterValue = filter.trim().toLowerCase();
      // name
      const name = data.name;
      // extensions
      const ext = this.formatExtensions.transform(data.extensions).join(", ");
      // destination
      const destination = data.foreignAddress;

      return (
        name.toLowerCase().includes(filterValue) ||
        ext.toLowerCase().includes(filterValue) ||
        destination.includes(filterValue)
      );
    };
  }
}
